// client/src/pages/About.tsx
import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AboutSEO } from './SEOHelpers';
import { Card, CardContent } from '../components/ui/card';
import { 
  TrendingUp,
  LineChart,
  Users,
  Globe,
  Award,
  BarChart2,
  Database,
  Code2,
  ExternalLink,
  ChevronRight,
  Zap,
  Brain,
  BarChart,
  ArrowRight,
  Hexagon,
  HelpCircle,
  Sparkles,
  Lightbulb
} from 'lucide-react';
// Removed SEO import - handled through LayoutSEO

interface TeamMember {
  name: string;
  role: string;
  image: string;
  linkedIn: string;
  background: string;
}

interface Milestone {
  year: string;
  title: string;
  description: string;
}

interface Feature {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
}

export default () => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<string>('features');
  const [animateItems, setAnimateItems] = useState(false);
  
  useEffect(() => {
    // Trigger animations after component mounts
    setTimeout(() => {
      setAnimateItems(true);
    }, 100);
  }, []);

  const stats = [
    {
      value: '1.5K+',
      label: 'Trading Widgets Created',
      icon: LineChart
    },
    {
      value: '90+',
      label: 'Active Users',
      icon: Users
    },
    {
      value: '12+',
      label: 'Countries',
      icon: Globe
    },
    {
      value: '99.7%',
      label: 'API Uptime',
      icon: TrendingUp
    }
  ];
  
  const features: Feature[] = [
    {
      title: 'Natural Language Interface',
      description: 'Create complex visualizations with simple conversations - no coding required.',
      icon: Brain
    },
    {
      title: 'Real-time Market Data',
      description: 'Access up-to-the-minute data from major exchanges and financial APIs.',
      icon: TrendingUp
    },
    {
      title: 'Custom Trading Widgets',
      description: 'Generate professional technical analysis visuals tailored to your needs.',
      icon: BarChart
    },
    {
      title: 'AI-Powered Insights',
      description: 'Get intelligent analysis and recommendations based on market patterns.',
      icon: Zap
    },
    {
      title: 'Data Visualization',
      description: 'Transform complex data into clear, actionable visual representations.',
      icon: BarChart2
    },
    {
      title: 'Cross-Platform Access',
      description: 'Use your tools seamlessly across desktop, tablet and mobile devices.',
      icon: Globe
    }
  ];

  const teamMembers: TeamMember[] = [
    {
      name: 'Nik Zral',
      role: 'CEO & Founder',
      image: '/team/nik-zral.jpeg',
      linkedIn: 'https://linkedin.com/in/nikzral',
      background: 'UVic Gustavson BCom Student with a passion for AI-powered financial technology'
    },
    
  ];

  const milestones: Milestone[] = [
    {
      year: '2021',
      title: 'Foundation',
      description: 'Newsroom founded at University of Victoria with a vision to democratize stock analysis through AI'
    },
    {
      year: '2022',
      title: 'Prototype',
      description: 'Developed initial prototype with basic natural language processing for market data analysis'
    },
    {
      year: '2023',
      title: 'Web Application',
      description: 'Launched web application with interactive trading widgets and real-time market data integration'
    },
    {
      year: '2024',
      title: 'Beta Program',
      description: 'Expanded feature set and launched limited beta access program for early adopters'
    },
    {
      year: '2025',
      title: 'Official Launch',
      description: 'Released full platform with enhanced AI capabilities and comprehensive stock analytics tools'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      } 
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* SEO Component */}
      <AboutSEO />

      {/* Hero Section with AI-inspired design */}
      <div className="relative overflow-hidden">
        {/* Dynamic Background */}
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-[linear-gradient(to_right,#232323_1px,transparent_1px),linear-gradient(to_bottom,#232323_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]' 
            : 'bg-[linear-gradient(to_right,#e5e5e5_1px,transparent_1px),linear-gradient(to_bottom,#e5e5e5_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#fff_70%,transparent_100%)]'
        }`} />
        
        {/* Gradient Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute -left-20 -top-20 w-72 h-72 rounded-full ${
            isDarkMode ? 'bg-blue-600/20' : 'bg-blue-400/10'
          } blur-3xl`} />
          <div className={`absolute right-0 bottom-0 w-96 h-96 rounded-full ${
            isDarkMode ? 'bg-purple-600/20' : 'bg-purple-400/10'
          } blur-3xl`} />
        </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <motion.div 
          className="relative mt-1 overflow-hidden py-16 md:py-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h1 className="text-4xl md:text-6xl font-bold font-heading tracking-tighter mb-6">
                  <span className="text-white">Our Mission to Democratize </span>
                  <span className="block mt-1.5 bg-gradient-to-r from-blue-400 via-violet-400 to-purple-400 bg-clip-text text-transparent">
                    Stock Analysis
                  </span>
                </h1>
              </motion.div>
              <motion.p 
                className="text-xl mb-8 text-gray-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Create professional-grade trading visualizations and get comprehensive market insights 
                through simple conversation with our AI-powered research assistant.
              </motion.p>
            </div>
          </div>
        </motion.div>

        {/* Stats Section */}
        <div className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div 
                    key={index} 
                    className="relative"
                    variants={itemVariants}
                  >
                    <div className="bg-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 text-center h-full group hover:border-blue-500/50 transition-all duration-300 hover:bg-blue-900/10">
                      <div className="flex justify-center mb-4">
                        <div className="p-3 rounded-lg bg-blue-600/20 border border-blue-500/30 group-hover:bg-blue-600/30 transition-all duration-300">
                          <Icon className="w-6 h-6 text-blue-400" />
                        </div>
                      </div>
                      <div className="text-3xl font-bold mb-2 text-white font-heading tracking-tight">
                        {stat.value}
                      </div>
                      <div className="text-gray-400 font-medium">
                        {stat.label}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>

        {/* Our Story Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-heading tracking-tighter">
                <span className="text-white">Our </span>
                <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                  Story
                </span>
              </h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  Founded in 2021 at the University of Victoria, Newsroom began with a clear mission: 
                  to make advanced stock analysis tools accessible to everyday traders and investors who 
                  lack the resources of large financial institutions.
                </p>
                <p>
                  Our AI-powered platform bridges this gap by allowing users to generate professional-grade 
                  trading visualizations and analysis widgets through natural language prompts, eliminating 
                  the need for complex programming or expensive software subscriptions.
                </p>
                <p>
                  Today, we're growing our user base across North America, helping individual investors 
                  make more informed decisions with custom trading visualizations, comprehensive market data, 
                  and AI-driven analysis tools.
                </p>
              </div>
            </motion.div>
            <motion.div 
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {[BarChart2, Database, Code2, Award].map((Icon, index) => (
                <div
                  key={index}
                  className={`aspect-square rounded-xl bg-gray-800/40 border border-gray-700/50 shadow-lg flex items-center justify-center group hover:border-purple-500/50 transition-all duration-300 hover:bg-purple-900/10`}
                >
                  <Icon className="w-12 h-12 text-purple-400 group-hover:text-purple-300 transition-colors duration-300" />
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-24 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-heading tracking-tighter mb-4">
                <span className="text-white">Powerful </span>
                <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                  Features
                </span>
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Our platform offers innovative tools designed to transform complex financial data into actionable insights.
              </p>
            </motion.div>
            
            <motion.div 
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div 
                    key={index}
                    variants={itemVariants}
                    className="bg-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 group hover:border-blue-500/50 transition-all duration-300 hover:bg-blue-900/10"
                  >
                    <div className="mb-4">
                      <div className="p-3 inline-flex rounded-lg bg-blue-600/20 border border-blue-500/30 group-hover:bg-blue-600/30 transition-all duration-300">
                        <Icon className="w-6 h-6 text-blue-400" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400">
                      {feature.description}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>

        {/* Timeline Section */}
        <div className="py-24 relative">
          <div className="absolute left-0 right-0 h-full">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-px h-full bg-blue-600/50" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold font-heading tracking-tighter mb-4">
                <span className="text-white">Our </span>
                <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                  Journey
                </span>
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                The road to revolutionizing financial data analysis with AI
              </p>
            </motion.div>
            
            <div className="space-y-16 relative">
              {milestones.map((milestone, index) => (
                <motion.div 
                  key={index} 
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className={`flex ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                    <div className={`relative ${index % 2 === 0 ? 'md:text-right' : ''} md:w-1/2 ${index % 2 === 0 ? 'md:pr-16' : 'md:pl-16'}`}>
                      <div className="absolute left-1/2 transform -translate-x-1/2 md:left-auto md:translate-x-0 md:-translate-y-1/2 md:top-1/2 md:-ml-12 md:mr-4 flex md:flex-col items-center z-10">
                        <div className="w-6 h-6 rounded-full bg-blue-600 border-4 border-gray-900 mb-2"/>
                        <div className="text-blue-400 font-bold text-xl">{milestone.year}</div>
                      </div>
                      
                      <div className="bg-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 relative group hover:border-blue-500/30 transition-all duration-300">
                        <h3 className="text-xl font-bold mb-2 text-white font-heading tracking-tighter">
                          {milestone.title}
                        </h3>
                        <p className="text-gray-400">
                          {milestone.description}
                        </p>
                        <div className="absolute inset-0 bg-blue-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Team Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold font-heading tracking-tighter mb-4">
              <span className="text-white">Meet Our </span>
              <span className="bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                Team
              </span>
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              The passionate people behind Newsroom's vision
            </p>
          </motion.div>
          
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden group hover:border-purple-500/30 transition-all duration-300"
              >
                <div className="h-72 relative overflow-hidden">
                  {member.image && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent z-10 opacity-70" />
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          target.src = '/team/placeholder.jpg';
                        }}
                      />
                    </>
                  )}
                  
                  {!member.image && (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <Users className="w-12 h-12 text-gray-500" />
                    </div>
                  )}
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-1 text-white">
                    {member.name}
                  </h3>
                  <div className="text-violet-400 mb-3">
                    {member.role}
                  </div>
                  <p className="text-gray-400 mb-4">
                    {member.background}
                  </p>
                  <a
                    href={member.linkedIn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center group/link text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    LinkedIn
                    <ExternalLink className="w-4 h-4 ml-1 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* CTA Section */}
        <div className="py-24 relative">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div 
              className="bg-gradient-to-b from-blue-900/20 to-violet-900/20 backdrop-blur-sm border border-blue-500/20 rounded-xl p-8 sm:p-12 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {/* Decorative elements */}
              <div className="absolute top-0 left-0 w-40 h-40 bg-blue-600/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-violet-600/10 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />
              
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white font-heading tracking-tighter relative z-10">
                Elevate Your Trading With AI-Powered Insights
              </h2>
              <p className="text-lg mb-8 text-gray-300 relative z-10">
                Join our growing community of investors who use Newsroom's 
                AI assistant to make smarter, data-driven trading decisions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
                <Link
                  to="/register"
                  className="px-8 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-500 hover:to-violet-500 transition-colors shadow-lg shadow-blue-900/30 hover:shadow-blue-900/50 flex items-center justify-center group"
                >
                  <span>Get Started Free</span>
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="px-8 py-3 rounded-lg bg-gray-800/80 border border-gray-700 text-white hover:bg-gray-700/80 transition-colors"
                >
                  Contact Sales
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};